import React, { useState, useReducer, useEffect } from "react";
import axios from "axios";
import { useShoppingContext } from "../../../../context/ShoppingContext";
import { useAuth } from "../../../../context/AuthContext";
import "./AddAlimentoForm.css";

// Reducer para manejar el estado del formulario
const formReducer = (state, action) => {
  return { ...state, [action.name]: action.value };
};

const AddAlimentoForm = () => {
  const {
    setShowAddAlimento,
    setAlimentos,
    setLoading,
    alimentos,
    fetchAlimentos,
    selectedAlimento, // Alimento seleccionado desde el contexto
    setSelectedAlimento,
  } = useShoppingContext();
  const { authenticatedUser } = useAuth();

  useEffect(() => {
    fetchAlimentos(); // Cargar los alimentos del usuario autenticado
  }, [fetchAlimentos]);

  // Estado inicial del formulario, usando el alimento seleccionado si existe
  const initialState = {
    nombre: selectedAlimento ? selectedAlimento.nombre : "",
    cantidad: "",
    unidadCantidad: "kg",
    pesoSaco: selectedAlimento ? selectedAlimento.pesoSaco : "",
    unidadPesoSaco: "kg",
    userId: authenticatedUser.id,
    alimentoExistenteId: selectedAlimento ? selectedAlimento.id : "", // Si existe, preselecciona el alimento
  };

  const [formState, dispatch] = useReducer(formReducer, initialState);
  const [nuevaCantidad, setNuevaCantidad] = useState(null); // Estado para la nueva cantidad

  // Maneja los cambios en los inputs del formulario
  const handleChange = (e) => {
    dispatch({ name: e.target.name, value: e.target.value });

    // Si se está actualizando la cantidad y ya se seleccionó un alimento existente, recalcula la nueva cantidad
    if (
      e.target.name === "cantidad" ||
      e.target.name === "alimentoExistenteId"
    ) {
      const alimentoExistente = alimentos.find(
        (alimento) => alimento.id === formState.alimentoExistenteId
      );
      const cantidadComprada = parseFloat(e.target.value) || 0;
      if (alimentoExistente) {
        const nuevaCantidadCalculada =
          parseFloat(alimentoExistente.cantidad) + cantidadComprada;
        setNuevaCantidad(nuevaCantidadCalculada.toFixed(2)); // Redondear a dos decimales
      } else {
        setNuevaCantidad(null); // Si no hay alimento seleccionado, no mostramos la nueva cantidad
      }
    }
  };

  // Maneja el envío del formulario
  const handleSubmit = (e) => {
    e.preventDefault();
    setLoading(true);

    if (formState.alimentoExistenteId) {
      // Si se seleccionó un alimento existente para agregar cantidad
      axios
        .put(
          `${process.env.REACT_APP_API_URL}/alimentos/${formState.alimentoExistenteId}/cantidad`,
          {
            cantidad: formState.cantidad,
            unidadCantidad: formState.unidadCantidad,
          }
        )
        .then((response) => {
          setAlimentos((prevAlimentos) =>
            prevAlimentos.map((alimento) =>
              alimento.id === response.data.alimento.id
                ? response.data.alimento
                : alimento
            )
          );
          setLoading(false);
        })
        .catch((error) =>
          console.error("Error al actualizar el alimento:", error)
        );
    } else {
      // Si se está creando un nuevo alimento
      axios
        .post(`${process.env.REACT_APP_API_URL}/alimentos`, formState)
        .then((response) => {
          setAlimentos((prevAlimentos) => [
            ...prevAlimentos,
            response.data.alimento,
          ]);
          setLoading(false);
        })
        .catch((error) =>
          console.error("Error al agregar el alimento:", error)
        );
    }

    setSelectedAlimento(null);
    setShowAddAlimento(false); // Cerrar el formulario
  };

  // Maneja la acción de cancelar
  const handleCancel = () => {
    setSelectedAlimento(null);
    setShowAddAlimento(false); // Cerrar el formulario al hacer clic en cancelar
  };

  // Filtrar alimentos solo para el authenticatedUser
  const alimentosFiltrados = alimentos.filter(
    (alimento) => alimento.userId === authenticatedUser.id
  );

  return (
    <div className="add-form-container">
      <form className="add-ave-form add-alimento-form" onSubmit={handleSubmit}>
        {/* Selección de alimento existente o creación de nuevo */}
        <div className="form-group">
          <label className="alimentoExistenteId" htmlFor="alimentoExistenteId">
            ¿Compraste alimento?
          </label>

          {/* Mostrar select solo si hay alimentos para este usuario */}
          {alimentosFiltrados.length > 0 && (
            <select
              name="alimentoExistenteId"
              value={formState.alimentoExistenteId}
              onChange={handleChange}
            >
              <option value="">
                {formState.alimentoExistenteId
                  ? `Nuevo Alimento`
                  : `Alimento existente`}
              </option>
              {alimentosFiltrados.map((alimento) => (
                <option key={alimento.id} value={alimento.id}>
                  {alimento.nombre + " | (" + alimento.cantidad + ") kg"}
                </option>
              ))}
            </select>
          )}
        </div>

        {/* Campo de nombre del alimento (se desactiva si se elige agregar a uno existente) */}
        {!formState.alimentoExistenteId && (
          <div className="form-group">
            <input
              type="text"
              name="nombre"
              placeholder="Nuevo Alimento"
              value={formState.nombre}
              onChange={handleChange}
              required={!formState.alimentoExistenteId}
            />
          </div>
        )}

        {/* Campo de cantidad comprada */}
        <div className="form-group">
          <input
            type="number"
            name="cantidad"
            placeholder={`Cantidad en ${formState.unidadCantidad}`}
            value={formState.cantidad}
            onChange={handleChange}
            inputMode="numeric"
            required
          />
        </div>

        {/* Selección de unidad para cantidad comprada */}
        <div className="form-group">
          <select
            name="unidadCantidad"
            value={formState.unidadCantidad}
            onChange={handleChange}
            required
          >
            <option value="kg">Kilogramos (kg)</option>
            <option value="ton">Toneladas (ton)</option>
            <option value="g">Gramos (g)</option>
            <option value="lb">Libras (lb)</option>
          </select>
        </div>

        {/* Mostrar nueva cantidad solo si se está agregando a un alimento existente */}
        {formState.alimentoExistenteId && nuevaCantidad !== null && (
          <p>
            Nueva cantidad después de agregar:{" "}
            <strong>{nuevaCantidad} kg</strong>
          </p>
        )}

        {/* Botones de enviar y cancelar */}
        <div className="add-ave-form-buttons">
          <button
            type="button"
            className="btn btn-green-outline"
            onClick={handleCancel}
          >
            Cancelar
          </button>
          <button type="submit" className="btn btn-green">
            {formState.alimentoExistenteId ? "Sumar" : "Agregar"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AddAlimentoForm;
